import { AxiosResponse } from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import {
  ValidationPredefinedData,
  ValidationStatusItem,
} from "../../slices/validation-status/Types";
import { validationStatusSliceAction } from "../../slices/validation-status";
import {
  ValidationStatusUpsertPayload,
  getValidationStatusListSagaAction,
  validationStatusSagaActionTypes,
} from "./saga-actions";

type ValidationStatusListAPIResponse = {
  success: boolean;
  data: ValidationStatusItem[];
};

const getValidationStatusListAPI = async () => {
  const response = await axiosRestInstance
    .get<AxiosResponse<ValidationStatusListAPIResponse>>(
      "/dummy-integration-validation"
    )
    .then((res) => res.data);
  return response;
};

type ValidationPredefinedDataAPIResponse = {
  success: boolean;
  partners?: { ID: number; partner_name: string }[];
  technologies?: Array<string | { value?: string; technology?: string }>;
  integrations?: Array<string | { value?: string; integration?: string }>;
};

const getValidationPredefinedDataAPI = async () => {
  const response = await axiosRestInstance
    .get<AxiosResponse<ValidationPredefinedDataAPIResponse>>(
      "/dummy-integration-validation/getPredefinedData"
    )
    .then((res) => res.data);
  return response;
};

type MutationAPIResponse = {
  success: boolean;
  message: string;
};

const addValidationStatusAPI = async (body: ValidationStatusUpsertPayload) => {
  const response = await axiosRestInstance
    .post<AxiosResponse<MutationAPIResponse>>(
      "/dummy-integration-validation/add",
      body
    )
    .then((res) => res.data);
  return response;
};

const updateValidationStatusAPI = async (
  ID: number,
  body: ValidationStatusUpsertPayload
) => {
  const response = await axiosRestInstance
    .put<AxiosResponse<MutationAPIResponse>>(
      `/dummy-integration-validation/update/${ID}`,
      body
    )
    .then((res) => res.data);
  return response;
};

const deleteValidationStatusAPI = async (ID: number) => {
  const response = await axiosRestInstance
    .delete<AxiosResponse<MutationAPIResponse>>(
      `/dummy-integration-validation/delete/${ID}`
    )
    .then((res) => res.data);
  return response;
};

const normalizeValidationStatus = (value: any): 0 | 1 => {
  if (value === 1 || value === "1" || value === true) return 1;
  return 0;
};

const sortValidationStatusByLatest = (list: ValidationStatusItem[]) =>
  [...list].sort((a, b) => Number(b.ID || 0) - Number(a.ID || 0));

function* getValidationStatusListSaga() {
  try {
    yield put(validationStatusSliceAction.setValidationStatusListLoading(true));
    const response: ValidationStatusListAPIResponse = yield call(
      getValidationStatusListAPI
    );

    const list = sortValidationStatusByLatest(
      (response?.data || []).map((item) => ({
        ...item,
        validation_status: normalizeValidationStatus(item.validation_status),
      }))
    );
    yield put(validationStatusSliceAction.setValidationStatusList(list));
  } catch (e) {
    yield put(validationStatusSliceAction.setValidationStatusList([]));
    console.log(e);
  } finally {
    yield put(validationStatusSliceAction.setValidationStatusListLoading(false));
  }
}

function* getValidationPredefinedDataSaga() {
  try {
    yield put(validationStatusSliceAction.setValidationPredefinedLoading(true));
    const response: ValidationPredefinedDataAPIResponse = yield call(
      getValidationPredefinedDataAPI
    );

    const partners = response?.partners || [];
    const technologies = (response?.technologies || [])
      .map((t: any) => (typeof t === "string" ? t : t?.value || t?.technology))
      .filter(Boolean);
    const integrations = (response?.integrations || [])
      .map((i: any) => (typeof i === "string" ? i : i?.value || i?.integration))
      .filter(Boolean);

    const predefinedData: ValidationPredefinedData = {
      partners,
      technologies,
      integrations,
    };
    yield put(validationStatusSliceAction.setValidationPredefinedData(predefinedData));
  } catch (e) {
    yield put(
      validationStatusSliceAction.setValidationPredefinedData({
        partners: [],
        technologies: [],
        integrations: [],
      })
    );
    console.log(e);
  } finally {
    yield put(validationStatusSliceAction.setValidationPredefinedLoading(false));
  }
}

function* addValidationStatusSaga(action: {
  type: string;
  payload: {
    body: ValidationStatusUpsertPayload;
    onSuccess?: () => void;
    onError?: (message: string) => void;
  };
}) {
  try {
    yield put(validationStatusSliceAction.setValidationAddLoading(true));
    const response: MutationAPIResponse = yield call(
      addValidationStatusAPI,
      action.payload.body
    );
    if (!response.success) {
      action.payload.onError?.(response.message || "Unable to add record.");
      return;
    }
    yield put(getValidationStatusListSagaAction());
    action.payload.onSuccess?.();
  } catch (e: any) {
    action.payload.onError?.(
      e?.response?.data?.message || "Unable to add record."
    );
  } finally {
    yield put(validationStatusSliceAction.setValidationAddLoading(false));
  }
}

function* updateValidationStatusSaga(action: {
  type: string;
  payload: {
    ID: number;
    body: ValidationStatusUpsertPayload;
    onSuccess?: () => void;
    onError?: (message: string) => void;
  };
}) {
  try {
    yield put(validationStatusSliceAction.setValidationUpdateLoading(true));
    const response: MutationAPIResponse = yield call(
      updateValidationStatusAPI,
      action.payload.ID,
      action.payload.body
    );
    if (!response.success) {
      action.payload.onError?.(response.message || "Unable to update record.");
      return;
    }
    yield put(getValidationStatusListSagaAction());
    action.payload.onSuccess?.();
  } catch (e: any) {
    action.payload.onError?.(
      e?.response?.data?.message || "Unable to update record."
    );
  } finally {
    yield put(validationStatusSliceAction.setValidationUpdateLoading(false));
  }
}

function* deleteValidationStatusSaga(action: {
  type: string;
  payload: {
    ID: number;
    onSuccess?: () => void;
    onError?: (message: string) => void;
  };
}) {
  try {
    yield put(validationStatusSliceAction.setValidationDeleteLoading(true));
    const response: MutationAPIResponse = yield call(
      deleteValidationStatusAPI,
      action.payload.ID
    );
    if (!response.success) {
      action.payload.onError?.(response.message || "Unable to delete record.");
      return;
    }
    yield put(getValidationStatusListSagaAction());
    action.payload.onSuccess?.();
  } catch (e: any) {
    action.payload.onError?.(
      e?.response?.data?.message || "Unable to delete record."
    );
  } finally {
    yield put(validationStatusSliceAction.setValidationDeleteLoading(false));
  }
}

function* validationStatusSaga() {
  yield takeLatest(
    validationStatusSagaActionTypes.callGetValidationStatusListSaga,
    getValidationStatusListSaga
  );
  yield takeLatest(
    validationStatusSagaActionTypes.callGetValidationPredefinedDataSaga,
    getValidationPredefinedDataSaga
  );
  yield takeLatest(
    validationStatusSagaActionTypes.callAddValidationStatusSaga,
    addValidationStatusSaga
  );
  yield takeLatest(
    validationStatusSagaActionTypes.callUpdateValidationStatusSaga,
    updateValidationStatusSaga
  );
  yield takeLatest(
    validationStatusSagaActionTypes.callDeleteValidationStatusSaga,
    deleteValidationStatusSaga
  );
}

export default validationStatusSaga;

